import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { ensureBillingCustomer } from "../../modals/billing-country-modal/ensure-billing-customer";
import { LicencePriceComponent } from "../../components/licence-price/licence-price.component";
import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
  EPageLayout,
  ESeverity,
  IModalService,
  IToastService,
  IconComponent,
  PageLayoutComponent,
  modalServiceInjectionToken,
  toastServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import { ZTemplateDirective } from "@zaeper/angular-core-lib";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { TagModule } from "primeng/tag";
import { TableModule } from "primeng/table";
import { TooltipModule } from "primeng/tooltip";
import { MenuItem } from "primeng/api";
import { ISubscription } from "../../models/i-subscription";
import { IPaymentMethod } from "../../models/i-payment-method";
import { IDeveloperBillingRestClient } from "../../rest-clients/billing/i-developer-billing-rest.client";
import { developerBillingRestClientInjectionToken } from "../../rest-clients/billing/developer-billing-rest-client-injection-token";
import { AddPaymentMethodModalComponent } from "../../modals/add-payment-method-modal/add-payment-method-modal.component";
import { StripeService } from "../../services/stripe/stripe.service";
import type { Stripe, StripeElements, StripePaymentElement } from "@stripe/stripe-js";

@Component({
  selector: "app-developer-license-page",
  templateUrl: "./developer-license-page.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    TagModule,
    TableModule,
    TooltipModule,
    ZTemplateDirective,
    PageLayoutComponent,
    IconComponent,
    LicencePriceComponent
  ]
})
export class DeveloperLicensePageComponent implements OnInit, OnDestroy {
  @ViewChild("paymentElement") paymentElementRef!: ElementRef<HTMLDivElement>;

  public readonly ePageLayout: typeof EPageLayout = EPageLayout;

  public readonly navigation: MenuItem[] = [
    {
      icon: "pi pi-home",
      routerLink: "/de/"
    },
    {
      label: "Entwicklerlizenz",
      routerLink: "/de/license"
    }
  ];

  public subscriptions: ISubscription[] = [];
  public paymentMethods: IPaymentMethod[] = [];
  public isLoading: boolean = true;
  public isSubscribing: boolean = false;
  public isLoadingPayment: boolean = false;
  public isConfirmingPayment: boolean = false;
  public paymentErrorMessage: string | null = null;

  private _stripe: Stripe | null = null;
  private _elements: StripeElements | null = null;
  private _paymentElement: StripePaymentElement | null = null;
  private _pendingStripeSubscriptionId: string | null = null;

  constructor(
    @Inject(developerBillingRestClientInjectionToken) private readonly _billingRestClient: IDeveloperBillingRestClient,
    @Inject(modalServiceInjectionToken) private readonly _modalService: IModalService,
    @Inject(toastServiceInjectionToken) private readonly _toastService: IToastService,
    private readonly _stripeService: StripeService,
    private readonly _cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this._loadData();
  }

  ngOnDestroy(): void {
    this._paymentElement?.destroy();
  }

  public get activeLicense(): ISubscription | null {
    return this.subscriptions.find(s => s.status === "ACTIVE" || s.status === "TRIALING") ?? null;
  }

  public getStatusSeverity(status: string): "success" | "warn" | "danger" {
    switch (status) {
      case "ACTIVE":
      case "TRIALING":
        return "success";
      case "PAST_DUE":
      case "INCOMPLETE":
        return "warn";
      case "CANCELED":
      case "UNPAID":
        return "danger";
      default:
        return "warn";
    }
  }

  public getStatusLabel(status: string): string {
    switch (status) {
      case "ACTIVE":
        return "Aktiv";
      case "TRIALING":
        return "Testphase";
      case "PAST_DUE":
        return "Zahlung ausstehend";
      case "CANCELED":
        return "Gekündigt";
      case "INCOMPLETE":
        return "Unvollständig";
      case "UNPAID":
        return "Unbezahlt";
      default:
        return status;
    }
  }

  public formatDate(epochSeconds: number): string {
    return new Date(epochSeconds * 1000).toLocaleDateString("de-DE");
  }

  public async addPaymentMethod(): Promise<void> {
    // A payment method belongs to the billing customer, which needs the billing country first.
    if (!(await ensureBillingCustomer(this._modalService, this._billingRestClient))) {
      return;
    }
    this._modalService.open(AddPaymentMethodModalComponent, {
      styleClass: "bg-white rounded-lg",
      header: "Zahlungsmethode hinzufügen",
      showHeader: true,
      closable: true,
      width: "550px",
      closeOnEscape: true,
      dismissableMask: true,
      data: {
        onSaveCallback: () => this._loadData()
      }
    });
  }

  public setDefaultPaymentMethod(method: IPaymentMethod): void {
    this._billingRestClient.setDefaultPaymentMethod(method.stripePaymentMethodId).then(() => {
      this._toastService.setMessage({
        title: "Erfolgreich",
        detail: "Standard-Zahlungsmethode wurde aktualisiert",
        severity: ESeverity.success
      });
      this._loadPaymentMethods();
    });
  }

  public deletePaymentMethod(method: IPaymentMethod): void {
    this._billingRestClient.detachPaymentMethod(method.stripePaymentMethodId).then(() => {
      this._toastService.setMessage({
        title: "Erfolgreich",
        detail: "Zahlungsmethode wurde entfernt",
        severity: ESeverity.success
      });
      this._loadPaymentMethods();
    });
  }

  public async subscribe(): Promise<void> {
    if (this.isSubscribing) return;
    if (!(await ensureBillingCustomer(this._modalService, this._billingRestClient))) {
      return;
    }

    this.isSubscribing = true;
    this.isLoadingPayment = true;
    this.paymentErrorMessage = null;
    this._cdr.markForCheck();

    try {
      this._stripe = await this._stripeService.getStripe();
      if (!this._stripe) {
        this.paymentErrorMessage = "Zahlungsanbieter konnte nicht geladen werden.";
        this.isLoadingPayment = false;
        this._cdr.markForCheck();
        return;
      }

      // The backend bills the yearly developer licence; the price is never chosen here.
      const subscription = await this._billingRestClient.subscribeLicense({
        items: [{
          productKey: "developer.license",
          quantity: 1
        }]
      });

      this._pendingStripeSubscriptionId = subscription.stripeSubscriptionId;

      if (!subscription.clientSecret) {
        this.paymentErrorMessage = "Zahlung konnte nicht initialisiert werden.";
        this.isLoadingPayment = false;
        this._cdr.markForCheck();
        return;
      }

      this._elements = this._stripe.elements({
        clientSecret: subscription.clientSecret,
        appearance: {
          theme: "flat",
          variables: {
            fontFamily: "inherit",
            borderRadius: "8px"
          }
        }
      });

      this._paymentElement = this._elements.create("payment", {
        layout: "tabs"
      });

      this._cdr.markForCheck();

      setTimeout(() => {
        this._paymentElement!.mount(this.paymentElementRef.nativeElement);
        this.isLoadingPayment = false;
        this._cdr.markForCheck();
      });
    } catch (e) {
      // A licence is already active: show it instead of a second subscription.
      if (e instanceof HttpErrorResponse && e.status === 409 && e.error?.error === "already_licensed") {
        this.cancelSubscribe();
        this._loadData();
        return;
      }
      console.error("Payment form loading failed:", e);
      this.paymentErrorMessage = "Zahlungsformular konnte nicht geladen werden.";
      this.isLoadingPayment = false;
      this._cdr.markForCheck();
    }
  }

  public async confirmPayment(): Promise<void> {
    if (!this._stripe || !this._elements || this.isConfirmingPayment) return;

    this.isConfirmingPayment = true;
    this.paymentErrorMessage = null;
    this._cdr.markForCheck();

    const { error } = await this._stripe.confirmPayment({
      elements: this._elements,
      confirmParams: {
        return_url: window.location.href
      },
      redirect: "if_required"
    });

    if (error) {
      this.paymentErrorMessage = error.message ?? "Zahlung fehlgeschlagen.";
      this.isConfirmingPayment = false;
      this._cdr.markForCheck();
    } else {
      try {
        if (this._pendingStripeSubscriptionId) {
          const synced = await this._billingRestClient.syncLicense(this._pendingStripeSubscriptionId);
          console.info("[DeveloperLicense] syncLicense returned", synced);
        }
        this._toastService.setMessage({
          title: "Erfolgreich",
          detail: "Deine Entwicklerlizenz ist jetzt aktiv.",
          severity: ESeverity.success
        });
      } catch (syncError) {
        console.error("[DeveloperLicense] syncLicense failed", syncError);
        this.paymentErrorMessage = "Lizenz konnte nach Zahlung nicht synchronisiert werden.";
        this._toastService.setMessage({
          title: "Synchronisation fehlgeschlagen",
          detail: "Die Zahlung war erfolgreich, aber die Lizenz konnte nicht aktiviert werden. Bitte lade die Seite neu.",
          severity: ESeverity.error
        });
      } finally {
        this.isSubscribing = false;
        this.isConfirmingPayment = false;
        this._pendingStripeSubscriptionId = null;
        this._paymentElement?.destroy();
        this._paymentElement = null;
        this._loadData();
      }
    }
  }

  public cancelSubscribe(): void {
    this.isSubscribing = false;
    this.isLoadingPayment = false;
    this.paymentErrorMessage = null;
    this._paymentElement?.destroy();
    this._paymentElement = null;
    this._cdr.markForCheck();
  }

  public cancelLicense(): void {
    const license = this.activeLicense;
    if (!license) return;

    this._billingRestClient.cancelLicense({
      stripeSubscriptionId: license.stripeSubscriptionId,
      cancelAtPeriodEnd: true
    }).then(() => {
      this._toastService.setMessage({
        title: "Erfolgreich",
        detail: "Lizenz wird zum Ende der Laufzeit gekündigt",
        severity: ESeverity.success
      });
      this._loadData();
    });
  }

  private _loadData(): void {
    this.isLoading = true;
    this._cdr.markForCheck();

    Promise.all([
      this._billingRestClient.getLicense().catch((e) => {
        console.error("[DeveloperLicense] getLicense failed", e);
        return [];
      }),
      this._billingRestClient.listPaymentMethods().catch((e) => {
        console.error("[DeveloperLicense] listPaymentMethods failed", e);
        return [];
      })
    ]).then(([subscriptions, paymentMethods]) => {
      console.info("[DeveloperLicense] _loadData subscriptions=", subscriptions);
      this.subscriptions = subscriptions;
      this.paymentMethods = paymentMethods;
      this.isLoading = false;
      this._cdr.markForCheck();
    });
  }

  private _loadPaymentMethods(): void {
    this._billingRestClient.listPaymentMethods().then((methods) => {
      this.paymentMethods = methods;
      this._cdr.markForCheck();
    }).catch(() => {});
  }
}
